import type { BedrockCapacity, BedrockGroupDetail, BedrockModel, BedrockModelFamily } from "../../api/bedrockModels";

/**
 * The client-side logic of the Bedrock Model Management page (AIA-109 phase 5): a local draft of the ids a group may invoke, grouping,
 * tabs, search and the live size estimate. It is pure so the page stays a thin shell; nothing here talks to the server or the browser
 * store, and the draft is only ever sent by an explicit Update.
 */

/** One model with the family and provider it is listed under. */
export interface ModelRow {
  model: BedrockModel;
  family: string;
  provider: string;
}

/** `all`, `enabled`, or `provider:<provider>`. */
export type TabKey = string;

export const allTab: TabKey = "all";
export const enabledTab: TabKey = "enabled";

const routePrefixes: readonly string[] = ["eu", "global", "us", "apac", "us-gov", "jp", "au", "ca"];

/** The provider of a model id: its first segment once a routing prefix (`eu.`, `global.`, ...) is taken off. `anthropic` for `eu.anthropic.claude-sonnet-4-6-v1:0`. */
export function providerOf(id: string): string {
  const parts = id.split(".");
  if (parts.length >= 3 && routePrefixes.includes(parts[0])) {
    return parts[1];
  }

  return parts.length >= 2 ? parts[0] : id;
}

/** A provider's name for a tab: the id segment with its first letter capitalised. */
export function providerLabel(provider: string): string {
  return provider.length === 0 ? provider : provider[0].toUpperCase() + provider.slice(1);
}

/** The tab key of a provider. */
export function providerTab(provider: string): TabKey {
  return `provider:${provider}`;
}

/** The DOM-safe form of a tab key, for element ids. */
export function tabDomId(tab: TabKey): string {
  return tab.replace(/[^A-Za-z0-9_-]/g, "-");
}

/** The DOM id of a tab button, which the panel it controls names as its label. */
export function modelTabId(key: TabKey): string {
  return `bedrock-tab-${tabDomId(key)}`;
}

/** The DOM id of the panel every tab controls. */
export const modelTabPanelId = "bedrock-tabpanel";

/** Flattens the families into rows, keeping the server's order. */
export function toRows(families: readonly BedrockModelFamily[]): ModelRow[] {
  return families.flatMap((family) => family.models.map((model) => ({ model, family: family.name, provider: providerOf(model.id) })));
}

/** The providers present in the rows, in name order. */
export function providersOf(rows: readonly ModelRow[]): string[] {
  return [...new Set(rows.map((row) => row.provider))].sort((a, b) => a.localeCompare(b));
}

/** Whether a row matches the search text: id, display name or family, without regard to case. An empty search matches everything. */
export function matchesSearch(row: ModelRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return true;
  }

  return [row.model.id, row.model.displayName, row.family].some((text) => text.toLowerCase().includes(needle));
}

/** The three parts of a group's selection: single ids, provider allows (`route:provider`) and exceptions (ids switched off inside a provider allow). */
export interface Selection {
  ids: ReadonlySet<string>;
  allows: ReadonlySet<string>;
  denied: ReadonlySet<string>;
}

/** The key of a provider allow in {@link Selection.allows}. */
export function allowKey(route: string, provider: string): string {
  return `${route}:${provider}`;
}

/** The empty selection. */
export function emptySelection(): Selection {
  return { ids: new Set(), allows: new Set(), denied: new Set() };
}

/** The routes a provider allow can be made on; a regional id is only ever allowed singly. */
export const allowRoutes: readonly string[] = ["eu", "global"];

/** The route of an id: the row's own when the catalogue lists it, else read from the id's prefix. */
function routeOfId(id: string, rows: readonly ModelRow[]): string {
  const row = rows.find((candidate) => candidate.model.id === id);
  if (row) {
    return row.model.route;
  }

  const prefix = id.split(".")[0];
  return allowRoutes.includes(prefix) ? prefix : "regional";
}

/** Whether a provider allow in the selection covers a row (same route and provider). */
export function isCovered(selection: Selection, row: ModelRow): boolean {
  return selection.allows.has(allowKey(row.model.route, row.provider));
}

/** Whether a row is switched on in the selection: a single id, or covered by a provider allow and not excepted. A model the catalogue no longer lists is never on through a provider allow. */
export function isOn(selection: Selection, row: ModelRow): boolean {
  if (selection.ids.has(row.model.id)) {
    return true;
  }

  return isCovered(selection, row) && row.model.available && !selection.denied.has(row.model.id);
}

/** True when the row is covered by a provider allow, so its switch means "except this model". */
export function isViaProvider(selection: Selection, row: ModelRow): boolean {
  return isCovered(selection, row);
}

function allowsOf(detail: BedrockGroupDetail): Set<string> {
  return new Set((detail.providerAllows ?? []).map((allow) => allowKey(allow.route, allow.provider)));
}

/**
 * The selection a group has now, read from the server: single ids that a provider allow already covers are dropped (the server refuses
 * them on save), and exceptions without a provider allow are dropped too. An exception for a model the catalogue does not list as
 * available is dropped as well: the renderer refuses an exception it cannot find in Bedrock's live listing (422 unknown_ids), and the
 * row of such a model cannot clear it. The change count compares against the raw server state, so the drop shows as an unsaved change.
 */
export function selectionOf(detail: BedrockGroupDetail): Selection {
  const allows = allowsOf(detail);
  const rows = toRows(detail.families);
  const covered = (id: string): boolean => allows.has(allowKey(routeOfId(id, rows), providerOf(id)));
  const available = new Set(rows.filter((row) => row.model.available).map((row) => row.model.id));

  return {
    ids: new Set(detail.allowedIds.filter((id) => !covered(id))),
    allows,
    denied: new Set((detail.deniedIds ?? []).filter((id) => covered(id) && available.has(id))),
  };
}

/** The selection exactly as the server reports it, for counting what the draft changes. */
function savedSelection(detail: BedrockGroupDetail): Selection {
  return { ids: new Set(detail.allowedIds), allows: allowsOf(detail), denied: new Set(detail.deniedIds ?? []) };
}

/** Whether a selection allows nothing at all (no single id and no provider allow), which needs the empty confirmation. */
export function isEmptySelection(selection: Selection): boolean {
  return selection.ids.size === 0 && selection.allows.size === 0;
}

/** Whether a row belongs to a tab; the search is applied separately. */
export function inTab(row: ModelRow, tab: TabKey, selection: Selection): boolean {
  if (tab === allTab) {
    return true;
  }

  if (tab === enabledTab) {
    return isOn(selection, row);
  }

  return providerTab(row.provider) === tab;
}

/** The rows a tab shows under the search. */
export function visibleRows(rows: readonly ModelRow[], tab: TabKey, selection: Selection, query: string): ModelRow[] {
  return rows.filter((row) => matchesSearch(row, query) && inTab(row, tab, selection));
}

/** The number of rows each tab would show under the search, by tab key. */
export function tabCounts(rows: readonly ModelRow[], providers: readonly string[], selection: Selection, query: string): Record<TabKey, number> {
  const matching = rows.filter((row) => matchesSearch(row, query));
  const counts: Record<TabKey, number> = {
    [allTab]: matching.length,
    [enabledTab]: matching.filter((row) => isOn(selection, row)).length,
  };
  for (const provider of providers) {
    counts[providerTab(provider)] = matching.filter((row) => row.provider === provider).length;
  }

  return counts;
}

/** A tab's rows grouped by family, in the order the families first appear. */
export function groupByFamily(rows: readonly ModelRow[]): { family: string; rows: ModelRow[] }[] {
  const groups: { family: string; rows: ModelRow[] }[] = [];
  for (const row of rows) {
    const existing = groups.find((group) => group.family === row.family);
    if (existing) {
      existing.rows.push(row);
    } else {
      groups.push({ family: row.family, rows: [row] });
    }
  }

  return groups;
}

function difference(a: ReadonlySet<string>, b: ReadonlySet<string>): { added: string[]; removed: string[] } {
  return { added: [...a].filter((item) => !b.has(item)), removed: [...b].filter((item) => !a.has(item)) };
}

/** The number of single ids, provider allows and exceptions that differ between the draft and what the group has now. */
export function changeCount(draft: Selection, detail: BedrockGroupDetail): number {
  const saved = savedSelection(detail);
  return [difference(draft.ids, saved.ids), difference(draft.allows, saved.allows), difference(draft.denied, saved.denied)].reduce(
    (total, part) => total + part.added.length + part.removed.length,
    0,
  );
}

/** The characters one id of a route adds to the policy, by the server's estimate. */
function estimateOf(capacity: BedrockCapacity, route: string): number {
  if (route === "eu") {
    return capacity.estimatePerId.eu;
  }

  return route === "global" ? capacity.estimatePerId.global : capacity.estimatePerId.regional;
}

/** The characters one provider allow on a route adds; zero when the server did not send an estimate. */
export function providerEstimate(capacity: BedrockCapacity, route: string): number {
  return route === "global" ? (capacity.estimatePerProvider?.global ?? 0) : (capacity.estimatePerProvider?.eu ?? 0);
}

/**
 * The policy size the draft would have: today's size, plus the estimate for each single id, provider allow and exception added and
 * minus it for each removed. An estimate only; the server measures the real document and its 422 is authoritative.
 */
export function estimatedSize(capacity: BedrockCapacity, rows: readonly ModelRow[], draft: Selection, detail: BedrockGroupDetail): number {
  const saved = savedSelection(detail);
  const ids = difference(draft.ids, saved.ids);
  const allows = difference(draft.allows, saved.allows);
  const denied = difference(draft.denied, saved.denied);
  const sum = (items: string[], cost: (item: string) => number): number => items.reduce((total, item) => total + cost(item), 0);
  const idCost = (id: string): number => estimateOf(capacity, routeOfId(id, rows));
  const allowCost = (key: string): number => providerEstimate(capacity, key.split(":")[0]);
  const exceptionCost = capacity.estimatePerException ?? 0;

  const size =
    capacity.used +
    sum(ids.added, idCost) -
    sum(ids.removed, idCost) +
    sum(allows.added, allowCost) -
    sum(allows.removed, allowCost) +
    (denied.added.length - denied.removed.length) * exceptionCost;
  return Math.max(0, size);
}

/**
 * The selection with one row switched on or off: a single id outside a provider allow, an exception inside one. A covered row the
 * catalogue no longer lists is already off and can never become an exception (the server refuses an exception it cannot resolve).
 */
export function toggleRow(selection: Selection, row: ModelRow, on: boolean): Selection {
  const id = row.model.id;
  const ids = new Set(selection.ids);
  const denied = new Set(selection.denied);
  if (isCovered(selection, row)) {
    ids.delete(id);
    if (!row.model.available) {
      return { ids, allows: selection.allows, denied };
    }

    if (on) {
      denied.delete(id);
    } else {
      denied.add(id);
    }
  } else if (on) {
    ids.add(id);
  } else {
    ids.delete(id);
  }

  return { ids, allows: selection.allows, denied };
}

/** The selection with several rows switched on or off. A row that is not listed any more cannot be switched on, and inside a provider allow it is never made an exception. */
export function toggleRows(selection: Selection, rows: readonly ModelRow[], on: boolean): Selection {
  return rows.reduce((current, row) => (on && !row.model.available ? current : toggleRow(current, row, on)), selection);
}

/**
 * The selection with a provider allowed or not on a route. Turning it on drops the single ids it now covers; turning it off removes
 * that provider's exceptions on the route, since an exception cannot exist without its allow.
 */
export function toggleProvider(selection: Selection, rows: readonly ModelRow[], route: string, provider: string, on: boolean): Selection {
  const covers = (id: string): boolean => routeOfId(id, rows) === route && providerOf(id) === provider;
  const allows = new Set(selection.allows);
  if (on) {
    allows.add(allowKey(route, provider));
    return { ids: new Set([...selection.ids].filter((id) => !covers(id))), allows, denied: selection.denied };
  }

  allows.delete(allowKey(route, provider));
  return { ids: selection.ids, allows, denied: new Set([...selection.denied].filter((id) => !covers(id))) };
}

/** The number of exceptions under one provider allow. */
export function exceptionsOf(selection: Selection, rows: readonly ModelRow[], route: string, provider: string): number {
  return [...selection.denied].filter((id) => routeOfId(id, rows) === route && providerOf(id) === provider).length;
}

/** The provider allows of a selection as `{route, provider}`, in a stable order, for sending. */
export function sortedAllows(selection: Selection): { route: string; provider: string }[] {
  return [...selection.allows]
    .sort((a, b) => a.localeCompare(b))
    .map((key) => {
      const split = key.indexOf(":");
      return { route: key.slice(0, split), provider: key.slice(split + 1) };
    });
}

/** The ids of a set in a stable order, for sending. */
export function sortedIds(ids: ReadonlySet<string>): string[] {
  return [...ids].sort((a, b) => a.localeCompare(b));
}
